import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Mail, ArrowRight, Sparkles, User, Users, MailCheck } from "lucide-react";
import { toast } from "sonner";
import api, { apiErr } from "../lib/api";
import { useAuth, roleHome } from "../context/AuthContext";
import { NeonLogo } from "../components/common";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";

const C = { cyan: "#00FFD1", magenta: "#FF2EA6", indigo: "#6366F1", green: "#39FF14" };

export default function Login() {
  const navigate = useNavigate();
  const { loginWithToken, user } = useAuth();
  const [role, setRole] = useState("child");
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [sent, setSent] = useState(null);
  const [loading, setLoading] = useState(false);

  // Al ingelogd? Dan nooit het inlogscherm tonen — direct door naar het dashboard.
  useEffect(() => {
    if (user) navigate(roleHome(user.role), { replace: true });
  }, [user, navigate]);

  const requestLink = async (e, presetEmail, presetRole) => {
    if (e) e.preventDefault();
    const mail = presetEmail || email;
    if (!mail) { toast.error("Vul een e-mailadres in"); return; }
    setLoading(true);
    try {
      const { data } = await api.post("/auth/magic-link", { email: mail, name, role: presetRole || role });
      if (data.token) {
        // Demo-account: meteen inloggen.
        const user = await loginWithToken(data.token);
        navigate(roleHome(user.role));
        return;
      }
      setSent({ email: mail });
      toast.success("E-mail verstuurd! 📬");
    } catch (err) {
      toast.error(apiErr(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const inputCls = "h-12 rounded-xl bg-white/5 border-2 border-white/10 text-white placeholder:text-slate-500 focus-visible:ring-[#00FFD1] focus-visible:border-[#00FFD1]";

  return (
    <div className="min-h-screen grid lg:grid-cols-2 font-outfit text-white" style={{ background: "#050507" }}>
      {/* Left visual */}
      <div className="hidden lg:flex flex-col justify-between p-12 relative overflow-hidden border-r border-white/10" style={{ background: "#0A0A0F" }}>
        <div className="absolute inset-0 neon-grid opacity-50" />
        <div className="glow-orb" style={{ width: 380, height: 380, background: C.cyan, top: -80, left: -60 }} />
        <div className="glow-orb" style={{ width: 320, height: 320, background: C.magenta, bottom: -60, right: -40 }} />
        <NeonLogo />
        <div className="relative">
          <Sparkles className="w-12 h-12 mb-6 text-glow-cyan" style={{ color: C.cyan }} />
          <h2 className="font-display text-4xl font-black leading-tight tracking-tight">Inloggen zonder <span className="neon-gradient-text">wachtwoord</span></h2>
          <p className="mt-4 text-lg text-slate-400 max-w-md">Vul je e-mail in, klik op de knop en duik direct in je avontuur. Veilig en simpel.</p>
        </div>
        <p className="relative text-slate-600 text-sm font-medium">© 2026 SlimAvontuur</p>
      </div>

      {/* Right form */}
      <div className="flex items-center justify-center p-6 sm:p-12 relative overflow-hidden">
        <div className="glow-orb lg:hidden" style={{ width: 300, height: 300, background: C.cyan, top: -100, right: -80 }} />
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-md relative z-10">
          <div className="lg:hidden mb-8"><NeonLogo /></div>

          {!sent ? (
            <>
              <h1 className="font-display text-3xl font-extrabold tracking-tight">Welkom terug! 👋</h1>
              <p className="mt-2 text-slate-400">Kies wie je bent en log in met een magische link.</p>

              <div className="mt-6 grid grid-cols-2 gap-2 p-1 rounded-2xl border border-white/10" style={{ background: "#0A0A0F" }}>
                {[{ k: "child", label: "Kind", icon: User }, { k: "parent", label: "Ouder", icon: Users }].map((r) => (
                  <button key={r.k} onClick={() => setRole(r.k)} data-testid={`role-${r.k}`}
                    className="flex items-center justify-center gap-2 py-2.5 rounded-xl font-bold transition-all"
                    style={role === r.k ? { background: C.cyan, color: "#000" } : { color: "#94A3B8" }}>
                    <r.icon className="w-4 h-4" /> {r.label}
                  </button>
                ))}
              </div>

              <form onSubmit={requestLink} className="mt-6 space-y-4">
                <div>
                  <Label className="font-bold text-slate-300">E-mailadres</Label>
                  <div className="relative mt-1.5">
                    <Mail className="absolute left-3.5 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
                    <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="jouw@email.nl"
                      className={`pl-11 ${inputCls}`} data-testid="email-input" />
                  </div>
                </div>
                <div>
                  <Label className="font-bold text-slate-300">Naam (optioneel)</Label>
                  <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Bijv. Lotte"
                    className={`mt-1.5 ${inputCls}`} data-testid="name-input" />
                </div>
                <Button type="submit" disabled={loading} className="w-full h-12 rounded-xl font-bold text-base text-black box-glow-cyan border-0 active:scale-95 hover:opacity-90" style={{ background: C.cyan }} data-testid="send-link-btn">
                  {loading ? "Bezig..." : "Stuur magische link"} <ArrowRight className="w-5 h-5 ml-1" />
                </Button>
              </form>
            </>
          ) : (
            <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="text-center" data-testid="email-sent-screen">
              <div className="w-20 h-20 rounded-3xl flex items-center justify-center mx-auto mb-6 box-glow-cyan" style={{ background: "rgba(0,255,209,0.12)" }}>
                <MailCheck className="w-10 h-10" strokeWidth={2.5} style={{ color: C.cyan }} />
              </div>
              <h1 className="font-display text-3xl font-extrabold tracking-tight">Check je e-mail! 📬</h1>
              <p className="mt-3 text-slate-400 max-w-sm mx-auto">
                We hebben een inloglink gestuurd naar <span className="font-bold text-slate-200">{sent.email}</span>.
                Klik op de link in de e-mail om in te loggen. (Kijk eventueel in je spam-map.)
              </p>
              <button onClick={() => setSent(null)} className="mt-8 text-sm font-bold text-slate-500 hover:text-slate-300" data-testid="back-btn">Ander e-mailadres gebruiken</button>
            </motion.div>
          )}
        </motion.div>
      </div>
    </div>
  );
}
